import React, { useState, useContext } from 'react';
import { useSearch, useNavigate } from '@tanstack/react-router';
import { AuthContext } from '@/context/auth.context';
import DashboardLayout from 'src/layouts/dashboard/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  CopyIcon,
  CheckIcon,
  ArrowSquareOutIcon,
  UsersIcon,
  CalendarIcon,
  TrashIcon,
  LinkIcon,
  ShareNetworkIcon,
  PencilIcon,
} from '@phosphor-icons/react';
import { formatDistanceToNow } from 'date-fns';
import {
  useSharedWithMe,
  useMySharedLinks,
  useDeleteShareableLink,
  getShareableLinkUrl,
  ShareableLinkWithCreator,
} from 'src/api/core/useShareableLinks';
import { TableSkeleton } from '@/components/common/loading-skeletons/table-skeleton';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { EditShareableLinkModal } from '@/components/reports/EditShareableLinkModal';

function formatResourceType(resourceType: string): string {
  // Convert "report:division-rebate-summary" to "Division Rebate Summary"
  if (resourceType.startsWith('report:')) {
    const reportId = resourceType.replace('report:', '');
    return reportId
      .split('-')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }
  return resourceType;
}

function getReportPath(resourceType: string): string | null {
  if (!resourceType.startsWith('report:')) return null;
  const reportId = resourceType.replace('report:', '');
  switch (reportId) {
    case 'division-rebate-summary':
      return '/rebates/reports/division-summary';
    case 'supplier-tag-summary':
      return '/rebates/reports/supplier-tag-summary';
    case 'daily-earnings-pye':
      return '/rebates/reports/daily-earnings-pye';
    default:
      return '/rebates/reports';
  }
}

function formatMetadata(metadata: Record<string, unknown>): string {
  const parts: string[] = [];

  if (metadata.year) {
    parts.push(`Year: ${metadata.year}`);
  }
  if (
    metadata.divisionIds &&
    Array.isArray(metadata.divisionIds) &&
    metadata.divisionIds.length > 0
  ) {
    parts.push(`${metadata.divisionIds.length} division(s)`);
  }
  if (metadata.lineType) {
    parts.push(`Type: ${metadata.lineType}`);
  }

  return parts.length > 0 ? parts.join(' • ') : 'No filters';
}

interface LinkCardProps {
  link: ShareableLinkWithCreator;
  showCreator?: boolean;
  showActions?: boolean;
  onDelete?: () => void;
  onEdit?: () => void;
}

function LinkCard({
  link,
  showCreator = false,
  showActions = false,
  onDelete,
  onEdit,
}: LinkCardProps) {
  const [copied, setCopied] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const handleCopy = async () => {
    const url = getShareableLinkUrl(link.token);
    await navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleOpen = () => {
    const url = getShareableLinkUrl(link.token);
    window.open(url, '_blank');
  };

  const handleDelete = () => {
    setDeleteDialogOpen(false);
    onDelete?.();
  };

  return (
    <>
      <Card>
        <CardContent className="p-4">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1 min-w-0 space-y-2">
              {/* Link name */}
              <div className="flex items-center gap-2">
                <LinkIcon className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                {link.name ? (
                  <span className="font-medium truncate">{link.name}</span>
                ) : (
                  <span className="text-muted-foreground italic">Unnamed</span>
                )}
              </div>

              {/* Report type - always shown as link */}
              <div className="text-sm">
                <span className="text-muted-foreground">Report: </span>
                <a
                  href={getReportPath(link.resourceType) || '#'}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:underline"
                >
                  {formatResourceType(link.resourceType)}
                </a>
              </div>

              {/* Recipients (for my shared links) */}
              {!showCreator && (
                <div className="flex items-center gap-2">
                  <UsersIcon className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                  <div className="flex flex-wrap gap-1">
                    {link.allowedEmails.slice(0, 3).map((email) => (
                      <Badge
                        key={email}
                        variant="secondary"
                        className="text-xs"
                      >
                        {email}
                      </Badge>
                    ))}
                    {link.allowedEmails.length > 3 && (
                      <Badge variant="outline" className="text-xs">
                        +{link.allowedEmails.length - 3} more
                      </Badge>
                    )}
                  </div>
                </div>
              )}

              {/* Metadata/Filters */}
              <div className="text-sm text-muted-foreground">
                {formatMetadata(link.metadata)}
              </div>

              {/* Created date and sharer */}
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <CalendarIcon className="h-3 w-3" />
                Created{' '}
                {formatDistanceToNow(new Date(link.createdAt), {
                  addSuffix: true,
                })}
                {showCreator && link.createdByUser && (
                  <span> • Shared by {link.createdByUser.name}</span>
                )}
                {link.expiresAt && (
                  <span className="text-amber-600">
                    {' '}
                    • Expires{' '}
                    {formatDistanceToNow(new Date(link.expiresAt), {
                      addSuffix: true,
                    })}
                  </span>
                )}
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-1 flex-shrink-0">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={handleOpen}
                    >
                      <ArrowSquareOutIcon className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Open link</TooltipContent>
                </Tooltip>
              </TooltipProvider>

              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={handleCopy}
                    >
                      {copied ? (
                        <CheckIcon className="h-4 w-4 text-green-600" />
                      ) : (
                        <CopyIcon className="h-4 w-4" />
                      )}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    {copied ? 'Copied!' : 'Copy link'}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>

              {showActions && onEdit && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={onEdit}
                      >
                        <PencilIcon className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Edit link</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}

              {showActions && onDelete && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-destructive hover:text-destructive"
                        onClick={() => setDeleteDialogOpen(true)}
                      >
                        <TrashIcon className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Delete link</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Delete confirmation dialog */}
      <AlertDialog
        open={deleteDialogOpen}
        onOpenChange={(open) => !open && setDeleteDialogOpen(false)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete shareable link?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete this link. Anyone who has this link
              will no longer be able to access the shared content.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDeleteDialogOpen(false)}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

function SharedWithMeTab() {
  const { data, isLoading, error } = useSharedWithMe();
  const links = data?.shareableLinks ?? [];

  if (isLoading) {
    return <TableSkeleton />;
  }

  if (error) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        Error loading shared links
      </div>
    );
  }

  if (links.length === 0) {
    return (
      <div className="text-center py-12">
        <ShareNetworkIcon className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
        <p className="text-muted-foreground">
          No reports have been shared with you yet.
        </p>
        <p className="text-sm text-muted-foreground mt-1">
          When someone shares a report with you, it will appear here.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {links.map((link) => (
        <LinkCard key={link.id} link={link} showCreator />
      ))}
    </div>
  );
}

function MySharedLinksTab() {
  const { data, isLoading, error, refetch } = useMySharedLinks();
  const links = data?.shareableLinks ?? [];
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [editingLink, setEditingLink] =
    useState<ShareableLinkWithCreator | null>(null);

  const handleDelete = async (linkId: string) => {
    setDeletingId(linkId);
    try {
      // We need to use the mutation directly
      const response = await fetch(`/api/core/shareable-links/${linkId}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (response.ok) {
        refetch();
      }
    } finally {
      setDeletingId(null);
    }
  };

  if (isLoading) {
    return <TableSkeleton />;
  }

  if (error) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        Error loading shared links
      </div>
    );
  }

  if (links.length === 0) {
    return (
      <div className="text-center py-12">
        <LinkIcon className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
        <p className="text-muted-foreground">
          You haven&apos;t shared any reports yet.
        </p>
        <p className="text-sm text-muted-foreground mt-1">
          Share a report from the Reports page to create shareable links.
        </p>
      </div>
    );
  }

  return (
    <>
      <div className="space-y-3">
        {links.map((link) => (
          <LinkCard
            key={link.id}
            link={link}
            showActions
            onEdit={() => setEditingLink(link)}
            onDelete={() => handleDelete(link.id)}
          />
        ))}
      </div>

      {/* Edit Modal */}
      {editingLink && (
        <EditShareableLinkModal
          open={!!editingLink}
          onClose={() => {
            setEditingLink(null);
            refetch();
          }}
          link={editingLink}
        />
      )}
    </>
  );
}

export function SharedLinksPage() {
  const { currentUser } = useContext(AuthContext);
  const isExternalShareUser = currentUser?.isExternalShareUser ?? false;
  const { tab } = useSearch({ strict: false }) as {
    tab: 'my-links' | 'shared-with-me';
  };
  const navigate = useNavigate();

  const handleTabChange = (value: string) => {
    navigate({
      to: '/shared-links',
      search: { tab: value as 'my-links' | 'shared-with-me' },
      replace: true,
    });
  };

  return (
    <DashboardLayout>
      <div className="p-6 w-full max-w-4xl">
        <div className="mb-6">
          <h1 className="text-2xl font-bold">Shared Reports</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {isExternalShareUser
              ? 'Reports that have been shared with you'
              : "View reports shared with you and manage links you've created"}
          </p>
        </div>

        {isExternalShareUser ? (
          <SharedWithMeTab />
        ) : (
          <Tabs value={tab} onValueChange={handleTabChange} className="w-full">
            <TabsList className="mb-4">
              <TabsTrigger value="my-links">My Shared Links</TabsTrigger>
              <TabsTrigger value="shared-with-me">Shared With Me</TabsTrigger>
            </TabsList>

            <TabsContent value="my-links">
              <MySharedLinksTab />
            </TabsContent>

            <TabsContent value="shared-with-me">
              <SharedWithMeTab />
            </TabsContent>
          </Tabs>
        )}
      </div>
    </DashboardLayout>
  );
}
